import type { ReactNode } from 'react';
import { Title } from '../../../../../components/Toolkit/InfoTitle/Title/Title';
import { media } from '../../../../../helpers/breakpoints';
import styled from 'styled-components';
import { Notification } from '../../../../../components/Toolkit/Notification/Notification';
import { GhostButton } from '../../../../../components/Toolkit/Button';
import { BarChartIc as BaseBarChartIc } from '@dsch/dd-icons';

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.S12};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M16};
  }
`;

const AdTitle = styled(Title)`
  max-height: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.S4};
  overflow: hidden;
`;

const TitleAndUserActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceAndActionsContainer = styled.div<{
  noPriceOrUnrealisticPrice?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: ${({ noPriceOrUnrealisticPrice }) =>
    noPriceOrUnrealisticPrice ? 'center' : 'flex-start'};

  height: ${({ noPriceOrUnrealisticPrice }) =>
    noPriceOrUnrealisticPrice ? '40px' : '100%'};

  margin-bottom: ${({ theme, noPriceOrUnrealisticPrice }) =>
    noPriceOrUnrealisticPrice ? 0 : theme.spacing.M16};

  ${media.medium} {
    margin-bottom: ${({ theme, noPriceOrUnrealisticPrice }) =>
      noPriceOrUnrealisticPrice ? 0 : theme.spacing.S12};
    align-items: ${({ noPriceOrUnrealisticPrice }) =>
      noPriceOrUnrealisticPrice ? 'center' : 'flex-end'};
  }
`;

const Price = styled.div<{ noPriceOrUnrealisticPrice?: boolean }>`
  ${({ theme, noPriceOrUnrealisticPrice }) =>
    noPriceOrUnrealisticPrice ? theme.fontSize.L18 : theme.fontSize.H32};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.S4};

  ${media.medium} {
    margin-bottom: 0;
  }
`;

const OldPrice = styled.span`
  ${({ theme }) => theme.fontSize.M16};
  color: ${({ theme }) => theme.colors.RED_DARK};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  position: relative;
  display: inline;

  &:after {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    right: 0;
    border-top: 1px solid;
    border-color: inherit;
    transform: rotate(-12.2deg);
  }
`;

const PricePerMonth = styled.div`
  margin: 0;

  ${media.medium} {
    margin: auto 0 0 ${({ theme }) => theme.spacing.S12};
  }
`;

const PriceAndDeliveryContainer = styled.div`
  display: flex;
  flex-direction: column;
  order: 1;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${media.medium} {
    flex-direction: row;
  }
`;

const Actions = styled.div<{
  isViewingOwnAd: boolean;
}>`
  position: relative;
  order: 2;
  margin-bottom: 0;

  ${media.medium} {
    margin-bottom: 0;
    order: 3;
  }
`;

const PositionedActions = styled.div`
  display: flex;
`;

const OwnAdButtonContainer = styled.div<{ renderBoostButton?: ReactNode }>`
  display: none;

  ${media.medium} {
    position: relative;
    top: 0;
    right: 0;
    display: grid;
    grid-template-columns: ${({ renderBoostButton }) =>
      renderBoostButton === undefined
        ? `repeat(1, 140px)`
        : `repeat(2, 140px)`};
    grid-column-gap: ${({ theme }) => theme.spacing.S8};
  }
`;

const ShareAdContainer = styled.div<{
  isViewingOwnAd: boolean;
}>`
  ${media.medium} {
    margin-right: ${({ isViewingOwnAd, theme }) =>
      !isViewingOwnAd ? theme.spacing.M16 : '0'};
  }
`;

const SoldTag = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const FinanceLinkWrapper = styled.div`
  order: 3;
  flex: 1 1 100%;
  font-family: 'Lato', sans-serif;
  display: flex;
  ${media.medium} {
    order: 4;
  }
`;

export const NextToPriceContainer = styled.div`
  border-left: solid ${({ theme }) => theme.colors.GREY_LIGHT} 1px;
  padding-left: ${({ theme }) => theme.spacing.M16};
  margin-left: ${({ theme }) => theme.spacing.M16};
  width: 115px;
`;

const OuterWrapper = styled.div`
  display: flex;
  order: 3;
  flex: 1 1 100%;

  ${media.medium} {
    order: 2;
    margin-left: ${({ theme }) => theme.spacing.M32};
    flex: 1 1 auto;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  position: relative;

  ${media.medium} {
    flex-wrap: nowrap;
    padding-top: ${({ theme }) => theme.spacing.S4};
    flex-grow: 0;
  }
`;

const SuccessWrapper = styled.div`
  width: 100%;
`;

const SNotification = styled(Notification)`
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
  padding: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    padding: ${({ theme }) =>
      `${theme.spacing.S8} ${theme.spacing.M16} ${theme.spacing.S8} ${theme.spacing.S12}`};
  }
`;

const AnalyticsContainer = styled.div`
  ${({ theme }) => theme.fontSize.M14};

  ${media.medium} {
    width: 163px;
  }
`;

const AnalyticsButton = styled(GhostButton)`
  margin-right: 0;

  ${media.medium} {
    margin-right: ${({ theme }) => theme.spacing.M16};
  }
`;

const BarChartIc = styled(BaseBarChartIc)`
  ${media.medium} {
    width: 20px;
    height: 20px;
  }
`;

const AnalyticsTextContainer = styled.p`
  display: none;

  ${media.medium} {
    display: block;
    margin-left: ${({ theme }) => theme.spacing.S4};
    color: ${({ theme }) => theme.colors.BLUE};
  }
`;

export {
  Header,
  AdTitle,
  TitleAndUserActionsContainer,
  PriceAndActionsContainer,
  PriceContainer,
  PriceAndDeliveryContainer,
  Price,
  OldPrice,
  PricePerMonth,
  Actions,
  PositionedActions,
  OwnAdButtonContainer,
  ShareAdContainer,
  SoldTag,
  FinanceLinkWrapper,
  OuterWrapper,
  InnerWrapper,
  SuccessWrapper,
  SNotification,
  AnalyticsContainer,
  AnalyticsTextContainer,
  AnalyticsButton,
  BarChartIc,
};
